import TimeSlotDropdown from "./components/time-slot-dropdown/TimeSlotDropdown";
import IntervalConnector from "../../../../../../../../../../interval-connector";
import styles from "./IntervalEditor.module.scss";
import { forwardRef, useRef, useImperativeHandle } from "react";

type TimeRange = {
  /** 24 hrs HH:mm */
  from: string;
  /** 24 hrs HH:mm */
  to: string;
};

type Props = {
  dateValue: number;
  min?: string;
  max?: string;
  interval: TimeRange;
  /** 24 hrs HH:mm */
  onChange: (arg: TimeRange) => void;
  /**
   * allow all time in the day
   * however, will respect the min and max
   */
  allowAllDay?: boolean;

  /**
   * if both from and too is required, default is all required
   */
  required?: { from: boolean; to: boolean };
};

/**
 * renders a UI that allow user to pick a time range, e.g. 5:00 AM - 9:00 AM
 */
const ForwardedIntervalEditor = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function IntervalEditor(
  {
    dateValue,
    interval,
    min,
    max,
    onChange,
    allowAllDay,
    required = { from: true, to: true },
  },
  ref,
) {
  const inputRefs = useRef<
    (Pick<HTMLFormElement, "checkValidity" | "reset"> | null)[]
  >([]);

  useImperativeHandle(ref, () => ({
    checkValidity: () => {
      const validities = inputRefs.current.map((el) => {
        if (!el) {
          return true;
        }

        return el.checkValidity();
      });

      return validities.every((el) => el);
    },
    reset: () =>
      inputRefs.current.forEach((el) => {
        if (!el) {
          return true;
        }
        return el.reset();
      }),
  }));

  return (
    <div className={styles["interval-editor"]}>
      <TimeSlotDropdown
        ref={(el) => {
          inputRefs.current[0] = el;
        }}
        required={required.from}
        toggleClass={styles["interval-editor__toggle"]}
        dateValue={dateValue}
        time={interval.from}
        min={min}
        max={allowAllDay ? max : interval.to}
        onChange={(from) =>
          onChange({
            ...interval,
            from,
          })
        }
        isFrom={true}
      />
      <IntervalConnector />
      <TimeSlotDropdown
        ref={(el) => {
          inputRefs.current[1] = el;
        }}
        required={required.to}
        toggleClass={styles["interval-editor__toggle"]}
        dateValue={dateValue}
        time={interval.to}
        min={allowAllDay ? min : interval.from}
        max={max}
        onChange={(to) =>
          onChange({
            ...interval,
            to,
          })
        }
      />
    </div>
  );
});

export default ForwardedIntervalEditor;
